import { Outlet, useLocation } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import React from "react";
import SeoHead from "./SeoHead";
import StructuredDataScripts from "../../seo/StructuredDataScripts";
import { captureAttribution } from "../../analytics/analytics";

export default function AppLayout() {
  const location = useLocation();
  const routeContentRef = React.useRef<HTMLDivElement | null>(null);
  const isFocusedFlow = /^\/(?:en|da|sv)\/(?:onboard|schedule)\/?$/.test(
    location.pathname
  );

  React.useEffect(() => {
    captureAttribution();
  }, [location.pathname, location.search]);

  React.useEffect(() => {
    if (isFocusedFlow || typeof window === "undefined") return;

    const rootElement = routeContentRef.current;
    if (!rootElement) return;
    const pageRoot: HTMLDivElement = rootElement;

    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) return;

    const animatedElements = new Set<HTMLElement>();
    const animationFrames: number[] = [];

    let sectionObserver: IntersectionObserver | null = null;

    if (typeof window.IntersectionObserver === "function") {
      sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add("is-visible");
            sectionObserver?.unobserve(entry.target);
          });
        },
        {
          rootMargin: "0px 0px -8% 0px",
          threshold: 0.12,
        }
      );
    }

    function getPageSections() {
      return Array.from(pageRoot.children).filter(
        (element): element is HTMLElement =>
          element instanceof HTMLElement &&
          element.tagName !== "SCRIPT" &&
          element.tagName !== "STYLE"
      );
    }

    function prepareSections() {
      const sections = getPageSections();
      const visibleNow: HTMLElement[] = [];

      sections.forEach((section, index) => {
        if (animatedElements.has(section)) return;

        animatedElements.add(section);
        section.classList.add("site-reveal");
        section.style.setProperty(
          "--site-reveal-delay",
          `${Math.min(index * 70, 210)}ms`
        );

        const rect = section.getBoundingClientRect();
        const isAlreadyInView =
          rect.top < window.innerHeight * 0.92 && rect.bottom > 0;

        if (!sectionObserver || isAlreadyInView) {
          visibleNow.push(section);
        } else {
          sectionObserver.observe(section);
        }
      });

      if (!visibleNow.length) return;

      const frame = window.requestAnimationFrame(() => {
        visibleNow.forEach((section) => section.classList.add("is-visible"));
      });
      animationFrames.push(frame);
    }

    prepareSections();

    const mutationObserver =
      typeof window.MutationObserver === "function"
        ? new MutationObserver(prepareSections)
        : null;

    mutationObserver?.observe(pageRoot, { childList: true });

    return () => {
      mutationObserver?.disconnect();
      sectionObserver?.disconnect();
      animationFrames.forEach((frame) => window.cancelAnimationFrame(frame));
      animatedElements.forEach((element) => {
        element.classList.remove("site-reveal", "is-visible");
        element.style.removeProperty("--site-reveal-delay");
      });
    };
  }, [isFocusedFlow, location.pathname]);

  if (isFocusedFlow) {
    return (
      <div className="min-h-screen">
        <SeoHead />
        <StructuredDataScripts />
        <main id="main-content">
          <React.Suspense fallback={null}>
            <Outlet />
          </React.Suspense>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <SeoHead />
      <StructuredDataScripts />
      <Header />
      <main id="main-content" className="flex-grow">
        <div
          key={location.pathname}
          ref={routeContentRef}
          className="site-route-in"
        >
          <React.Suspense fallback={null}>
            <Outlet />
          </React.Suspense>
        </div>
      </main>

      <Footer />
    </div>
  );
}
